import React, { useState, useEffect } from "react";
import { Layout } from "../components";
import { useParams, Link } from "react-router-dom";
import { fetchIndustryBySlug } from "../api/strapi/industries";
import { fetchDomains } from "../api/strapi/domains";
import { transformDomainsToMegaMenuStructure } from "../api/strapi/utils/domains-transformer";
import SectionGuard from "../components/SectionGuard";
import SectionLayout from "../components/SectionLayout";
import { useIntl } from "react-intl";
import DataLoader from "../components/loader/DataLoader";
import CaseStudiesDisplay from "../components/CaseStudiesDisplay";
import PageHero from "../components/common/PageHero";
import SectionHeader from "../components/common/SectionHeader";
import SkeletonImage from "../components/common/SkeletonImage";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

const IndustryPage = () => {
  const { slug } = useParams();
  const intl = useIntl();
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [industryResult, setIndustryResult] = useState(null);
  const [relatedItems, setRelatedItems] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(true);

  useEffect(() => {
    const loadIndustry = async () => {
      try {
        setLoading(true);
        const result = await fetchIndustryBySlug(slug);
        setIndustryResult(result);

        if (!result.ok) {
          if (result.reason !== "NOT_FOUND") {
            console.warn("Industry data unavailable:", result);
            setError(intl.formatMessage({ id: "common.contentUnavailable" }));
          }
          setIndustry(null);
          return;
        }

        setIndustry(result.data);
      } catch (err) {
        console.error("Erreur lors du chargement de l'industrie:", err);
        setError(intl.formatMessage({ id: "common.contentUnavailable" }));
      } finally {
        setLoading(false);
      }
    };

    loadIndustry();
  }, [slug, intl]);

  useEffect(() => {
    if (!industry?.domaine?.title) return;
    const loadRelated = async () => {
      try {
        const result = await fetchDomains();
        if (!result.ok) { setRelatedLoading(false); return; }
        const menuData = transformDomainsToMegaMenuStructure(result.data);
        const category = menuData.find((cat) => cat.type === "industries");
        if (category) {
          const related = category.submenus.filter(
            (item) => item.parentDomain === industry.domaine.title && item.slug !== slug
          );
          setRelatedItems(related);
        }
      } catch (err) {
        console.error("Erreur:", err);
      } finally {
        setRelatedLoading(false);
      }
    };
    loadRelated();
  }, [industry?.domaine?.title, slug]);

  if (loading) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.loading" }) }}>
        <DataLoader isLoading={true} />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.unavailable" }) }}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-gray-500 text-center">
            <h2 className="text-2xl font-bold mb-2">{intl.formatMessage({ id: "common.contentUnavailableTitle" })}</h2>
            <p>{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!industry) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.sectionUnavailable" }) }}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-gray-500 text-center">
            <h2 className="text-2xl font-bold mb-2">{intl.formatMessage({ id: "common.sectionUnavailable" })}</h2>
            <p>{intl.formatMessage({ id: "common.noContent" })}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const { title, heroComponent, sections, domaine } = industry;
  const sectionFallback = (
    <div className="py-10 text-center text-gray-500 text-sm">
      {intl.formatMessage({ id: "empty.section" })}
    </div>
  );

  return (
    <Layout data={{ title }}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <SectionGuard result={industryResult} fallback={sectionFallback} logLabel="Industry hero">
          {heroComponent && (
            <PageHero
              eyebrow="Africa Systems"
              title={heroComponent.title}
              description={heroComponent.description}
              image={`${process.env.REACT_APP_STRAPI_URL}${heroComponent.image.url}`}
            />
          )}
        </SectionGuard>

        {/* Domain Info */}
        <SectionGuard result={industryResult} fallback={null} logLabel="Industry domain">
          {domaine && (
            <div className="py-6 bg-neutral-50 border-b border-slate-200">
              <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
                <div className="flex items-center">
                  <div className="mr-4">
                    {domaine.icon && (
                      <SkeletonImage
                        className="w-12 h-12"
                        src={`${process.env.REACT_APP_STRAPI_URL}${domaine.icon.url}`}
                        alt={domaine.title}
                        imgClassName="w-full h-full object-contain"
                        icon={false}
                      />
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{domaine.title}</h2>
                    <p className="text-sm text-gray-600">{domaine.description}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SectionGuard>

        {/* Sections */}
        <div className="py-16">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
            {(sections || []).map((section, index) => (
              <div
                key={section.id}
                className={`mb-16 ${index % 2 === 0 ? "odd-section" : "even-section"}`}
              >
                <SectionLayout section={section} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Related Industries Section */}
      {!relatedLoading && relatedItems.length > 0 && (
        <section className="py-16 bg-neutral-50">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
            <SectionHeader
              eyebrow="Related Industries"
              title={industry.domaine.title}
              description={intl.formatMessage({ id: "related.industries.description", defaultMessage: "Découvrez nos autres solutions dans le même domaine." })}
              align="center"
            />
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={24}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              breakpoints={{ 640: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
              className="pb-12 !px-2"
            >
              {relatedItems.map((item, idx) => {
                const imageUrl = item.metaImage
                  ? `${process.env.REACT_APP_STRAPI_URL}${item.metaImage.url}`
                  : null;
                return (
                  <SwiperSlide key={item.documentId || idx}>
                    <Link
                      to={`/info/industries/${item.slug}`}
                      className="group h-full flex flex-col rounded-2xl bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 overflow-hidden"
                    >
                      <SkeletonImage
                        className="h-48"
                        src={imageUrl}
                        alt={item.title}
                        imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        overlay={<div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />}
                      >
                        <div className="w-full h-full flex items-center justify-center text-slate-400 italic text-sm">
                          {intl.formatMessage({ id: "common.noImage", defaultMessage: "Aucune image" })}
                        </div>
                      </SkeletonImage>
                      <div className="p-6 flex flex-col flex-grow">
                        <span className="text-xs font-semibold bg-[#16A34A]/10 text-[#15803D] px-2 py-1 rounded-full mb-2 inline-block w-fit">
                          {item.parentDomain}
                        </span>
                        <h3 className="text-lg font-bold text-[#0a1628] mb-2 group-hover:text-[#16A34A] transition-colors line-clamp-2">
                          {item.title}
                        </h3>
                        {item.metaDescription ? (
                          <p className="text-sm text-gray-600 line-clamp-3 mb-4 flex-grow">{item.metaDescription}</p>
                        ) : <div className="flex-grow" />}
                        <div className="pt-4 border-t border-slate-100 flex items-center text-[#16A34A] font-semibold text-sm">
                          {intl.formatMessage({ id: "common.readMore", defaultMessage: "En savoir plus" })}
                          <svg className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </div>
                      </div>
                    </Link>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </section>
      )}

      {/* Case Studies Section */}
      <CaseStudiesDisplay caseStudies={industry?.caseStudies || []} />
    </Layout>
  );
};

export default IndustryPage;
